import { useState, useEffect } from 'react';
import { getUserInsights } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Eye, TrendingUp, TrendingDown, Minus, Phone, BookOpen, 
  CheckCircle, AlertTriangle, Star, Target, Download,
  BarChart3, Award, Zap
} from 'lucide-react';

// Mini chart component for monthly trends
function MiniTrendChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const maxScore = 10;
  const height = 60;
  const width = 200;
  const padding = 5;
  
  const points = data.map((d, i) => {
    const x = padding + (i / (data.length - 1 || 1)) * (width - 2 * padding);
    const y = height - padding - ((d.average_score / maxScore) * (height - 2 * padding));
    return `${x},${y}`;
  }).join(' ');
  
  // Determine trend color
  const firstScore = data[0]?.average_score || 0;
  const lastScore = data[data.length - 1]?.average_score || 0;
  const trendColor = lastScore > firstScore + 0.3 ? '#22c55e' : 
                     lastScore < firstScore - 0.3 ? '#ef4444' : '#3b82f6';
  
  return (
    <svg width={width} height={height} className="mt-2">
      {/* Grid lines */}
      <line x1={padding} y1={height/2} x2={width-padding} y2={height/2} stroke="#334155" strokeDasharray="2,2" />
      
      {/* Trend line */}
      <polyline
        fill="none"
        stroke={trendColor}
        strokeWidth="2"
        points={points}
      />
      
      {/* Data points */}
      {data.map((d, i) => {
        const x = padding + (i / (data.length - 1 || 1)) * (width - 2 * padding);
        const y = height - padding - ((d.average_score / maxScore) * (height - 2 * padding));
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="4"
            fill={trendColor}
          />
        );
      })}
    </svg>
  );
}

// Strength/Improvement Item
function InsightItem({ item, type }) {
  const isStrength = type === 'strength';
  
  return (
    <div className={`p-3 rounded-lg ${isStrength ? 'bg-green-500/10 border border-green-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
      <div className="flex items-center gap-2">
        {isStrength ? (
          <Star className="h-4 w-4 text-green-500" />
        ) : (
          <Target className="h-4 w-4 text-amber-500" />
        )}
        <span className="font-medium text-sm">{item.area}</span>
        <Badge variant="outline" className={`ml-auto text-xs ${isStrength ? 'border-green-500/50 text-green-400' : 'border-amber-500/50 text-amber-400'}`}>
          {item.score}/10
        </Badge>
      </div>
    </div>
  );
}

export function UserInsightsPopup({ userId, userName, isOpen, onClose }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      fetchInsights();
    }
  }, [isOpen, userId]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUserInsights(userId);
      setInsights(response.data);
    } catch (err) {
      setError('Failed to load insights');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
      case 'up':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'declining':
      case 'down':
        return <TrendingDown className="h-5 w-5 text-red-500" />;
      default:
        return <Minus className="h-5 w-5 text-blue-500" />;
    }
  };

  const getTrendBadge = (trend) => {
    const styles = {
      improving: 'bg-green-500/20 text-green-400 border-green-500/30',
      up: 'bg-green-500/20 text-green-400 border-green-500/30',
      declining: 'bg-red-500/20 text-red-400 border-red-500/30',
      down: 'bg-red-500/20 text-red-400 border-red-500/30',
      consistent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      stable: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      new: 'bg-muted/500/20 text-muted-foreground border-slate-500/30',
      new_user: 'bg-muted/500/20 text-muted-foreground border-slate-500/30'
    };
    return styles[trend] || styles.consistent;
  };

  const handleDownload = () => {
    if (!insights) return;
    
    const reportData = {
      user: insights.user,
      generated_at: insights.generated_at,
      summary: {
        total_calls: insights.total_calls,
        average_score: insights.average_score,
        trend: insights.trend,
        trend_description: insights.trend_description
      },
      strengths: insights.top_strengths,
      improvement_areas: insights.improvement_areas,
      homework: {
        pending: insights.homework_pending,
        completed: insights.homework_completed
      },
      monthly_performance: insights.monthly_stats,
      compliance_issues: insights.compliance_issues
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${insights.user?.name || 'user'}_insights_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Eye className="h-5 w-5 text-blue-500" />
            {userName || 'User'} - Performance Insights
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 p-4">
            <Skeleton className="h-20 w-full bg-slate-800" />
            <Skeleton className="h-32 w-full bg-slate-800" />
            <Skeleton className="h-24 w-full bg-slate-800" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-400">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
            {error}
          </div>
        ) : insights ? (
          <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <Phone className="h-5 w-5 mx-auto text-blue-500 mb-1" />
                <p className="text-2xl font-bold text-white">{insights.total_calls}</p>
                <p className="text-xs text-muted-foreground">Total Calls</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <BarChart3 className="h-5 w-5 mx-auto text-green-500 mb-1" />
                <p className="text-2xl font-bold text-white">{insights.average_score}</p>
                <p className="text-xs text-muted-foreground">Avg Score</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <BookOpen className="h-5 w-5 mx-auto text-amber-500 mb-1" />
                <p className="text-2xl font-bold text-white">{insights.homework_pending}</p>
                <p className="text-xs text-muted-foreground">Homework Due</p>
              </div>
              <div className="bg-slate-800 rounded-lg p-3 text-center">
                <CheckCircle className="h-5 w-5 mx-auto text-emerald-500 mb-1" />
                <p className="text-2xl font-bold text-white">{insights.homework_completed}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>

            {/* Trend Section */}
            <div className="bg-slate-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  {getTrendIcon(insights.trend)}
                  Performance Trend
                </h3>
                <Badge variant="outline" className={getTrendBadge(insights.trend)}>
                  {insights.trend?.replace('_', ' ').toUpperCase()}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{insights.trend_description}</p>
              
              {insights.monthly_stats && insights.monthly_stats.length > 0 && (
                <div>
                  <MiniTrendChart data={insights.monthly_stats} />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    {insights.monthly_stats.map((m, i) => (
                      <span key={i}>{m.month?.substring(5)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Strengths & Improvements */}
            <div className="grid grid-cols-2 gap-4">
              {/* Strengths */}
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                  <Award className="h-4 w-4 text-green-500" />
                  Top Strengths
                </h3>
                <div className="space-y-2">
                  {insights.top_strengths?.length > 0 ? (
                    insights.top_strengths.map((s, i) => (
                      <InsightItem key={i} item={s} type="strength" />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No data yet</p>
                  )}
                </div>
              </div>

              {/* Improvements */}
              <div>
                <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-amber-500" />
                  Areas to Improve
                </h3>
                <div className="space-y-2">
                  {insights.improvement_areas?.length > 0 ? (
                    insights.improvement_areas.map((s, i) => (
                      <InsightItem key={i} item={s} type="improvement" />
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">No data yet</p>
                  )}
                </div>
              </div>
            </div>

            {/* Score Breakdown */}
            {insights.score_breakdown && Object.keys(insights.score_breakdown).length > 0 && (
              <div className="bg-slate-800 rounded-lg p-4">
                <h3 className="font-semibold text-white mb-3">Score Breakdown</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {Object.entries(insights.score_breakdown).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground w-32 truncate">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <Progress value={value * 10} className="flex-1 h-2" />
                      <span className="text-xs font-medium text-white w-8">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compliance Issues */}
            {insights.compliance_issues?.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  Compliance Alerts
                </h3>
                <div className="flex flex-wrap gap-2">
                  {insights.compliance_issues.map((issue, i) => (
                    <Badge key={i} variant="outline" className="border-red-500/50 text-red-400">
                      {issue.type?.replace(/_/g, ' ')} ({issue.count})
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Download Button */}
            <div className="flex justify-end pt-2 border-t border-slate-700">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownload}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Report
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// Small insight badge for user list (shows on hover of eye icon)
export function UserInsightsBadge({ userId, insights }) {
  if (!insights || !insights[userId]) {
    return null;
  }
  
  const data = insights[userId];
  
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-3 w-3 text-green-500" />;
      case 'down':
        return <TrendingDown className="h-3 w-3 text-red-500" />;
      default:
        return <Minus className="h-3 w-3 text-blue-500" />;
    }
  };
  
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">{data.total_calls} calls</span>
      <span className="text-muted-foreground">•</span>
      <span className={`font-medium ${data.avg_score >= 7 ? 'text-green-400' : data.avg_score >= 5 ? 'text-amber-400' : 'text-red-400'}`}>
        {data.avg_score}
      </span>
      {getTrendIcon(data.trend)}
    </div>
  );
}
